import { useEffect, useState } from 'react'
import { navigateUrl, useWakeLock } from '../platform/device'
import { cachedAccess, fetchStopAccess, referencePhotoObjectUrl, type StopAccess } from '../sync/access'
import { ITEM_ACCESS_FIELDS, itemState } from '../sync/checklistNav'
import { FIELD_TO_FOUND } from '../sync/findings'
import { putAccess } from '../sync/db'
import { getDraft, type StopDraft } from '../sync/drafts'
import type { DriverStop } from '../sync/route'
import { CONDITIONS, ITEM_TITLES, TAP, badge, bigButton, button, card, color, container, footer, h1, muted, page, textButton, tierColor, tierLabel } from '../ui'

/**
 * STOP DETAIL (v3 section 9): address, a large Navigate button that opens
 * Google Maps, access instructions as plain facts, the gate code in large
 * type, a dog warning as a red banner, the customer's reference photos
 * inline. Then one big button: Start checklist. Skip is one tap away.
 *
 * Every checklist item is a tappable row carrying the access note that says
 * where it is (Brad, field test 2026-09-14): standing at the feeder, tap
 * Feeder and the camera page for the feeder opens.
 *
 * FOUND FIRST (Brad, 2026-09-14). What a driver found sits beside what the
 * customer said and never replaces it; wherever both exist, the finding is shown
 * first and the customer's words under it.
 */
const DROP_LABEL: Record<string, string> = {
  front_door: 'Front door', back_door: 'Back door', garage: 'Garage', other: 'Other spot',
}

export function StopScreen({
  stop,
  onStart,
  onStartAt,
  onSkip,
  onFound,
  onBack,
}: {
  stop: DriverStop
  onStart: () => void
  onStartAt: (itemIndex: number) => void
  onSkip: () => void
  onFound: () => void
  onBack: () => void
}) {
  const [access, setAccess] = useState<StopAccess | null | undefined>(undefined)
  const [photoUrls, setPhotoUrls] = useState<Record<number, string>>({})
  const [draft, setDraft] = useState<StopDraft | undefined>(undefined)
  useWakeLock(true)

  useEffect(() => {
    let alive = true
    void getDraft(stop.uuid).then((d) => {
      if (alive) setDraft(d)
    })
    ;(async () => {
      let data = await cachedAccess(stop.uuid)
      if (!data && navigator.onLine) {
        try {
          data = await fetchStopAccess(stop.uuid)
          await putAccess(stop.uuid, data)
        } catch {
          /* no signal and nothing cached: say so below */
        }
      }
      if (!alive) return
      setAccess(data ?? null)
      if (data) {
        const urls: Record<number, string> = {}
        for (const p of data.reference_photos) urls[p.id] = await referencePhotoObjectUrl(stop.uuid, p)
        if (alive) setPhotoUrls(urls)
      }
    })()
    return () => {
      alive = false
      Object.values(photoUrls).forEach((u) => u.startsWith('blob:') && URL.revokeObjectURL(u))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stop.uuid])

  const a = access?.access ?? null
  const text = (field: string): string => {
    const v = a ? (a as unknown as Record<string, unknown>)[field] : ''
    return typeof v === 'string' ? v.trim() : ''
  }
  // What a driver found for the location this customer field describes.
  const foundText = (field: string): string => {
    const key = FIELD_TO_FOUND[field]
    return (key && access?.found?.[key]?.text?.trim()) || ''
  }

  const hasPin = stop.latitude != null && stop.longitude != null
  // Five decimals is about a metre; a true minus sign reads better than a hyphen.
  const pinText = (n: number) => n.toFixed(5).replace('-', '−')

  const required = stop.checklist_required
  // Notes that belong to a checklist item are shown on that item's row; the
  // rest (and Notes) stay as plain facts.
  const onItemRows = new Set(required.flatMap((t) => (ITEM_ACCESS_FIELDS[t] ?? []).map((f) => f.field)))
  const facts: [string, string, string][] = access
    ? ([
        ['Gate', 'gate_location'],
        ['Latch', 'latch_notes'],
        ['Feed storage', 'feed_storage_location'],
        ['Waterer', 'waterer_location'],
        ['Feeder', 'feeder_location'],
        ['Grit', 'grit_location'],
        ['Oyster shell', 'shell_location'],
        ['Notes', 'notes'],
      ] as [string, string][])
        .filter(([, field]) => !onItemRows.has(field))
        .map(([label, field]) => [label, foundText(field), text(field)] as [string, string, string])
        .filter(([, found, said]) => found || said)
    : []

  return (
    <div style={page}>
      <div style={container}>
        <button style={{ ...textButton, paddingLeft: 0 }} onClick={onBack}>‹ Route</button>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <span style={badge(tierColor(stop.service_tier))}>{tierLabel(stop.service_tier)}</span>
          <span style={muted}>{stop.item_count} bag{stop.item_count === 1 ? '' : 's'}</span>
        </div>
        <div style={h1}>{stop.name}</div>
        <div style={{ fontSize: 20, marginBottom: 12 }}>{stop.address}, {stop.city} {stop.postal_code}</div>

        <a href={navigateUrl(stop)} target="_blank" rel="noreferrer" style={{ ...bigButton(color.blue), textDecoration: 'none', textAlign: 'center', marginBottom: hasPin ? 8 : 16, boxSizing: 'border-box' }}>
          Navigate ▸
        </a>
        {hasPin && (
          // Navigate sends the pin, not the address text, and Google labels a
          // pin with whatever address it thinks is nearest - which can differ
          // from the address above. Say what the driver is actually sent to.
          <div style={{ ...muted, fontSize: 15, marginBottom: 16 }}>
            Navigates to the map pin ({pinText(stop.latitude as number)}, {pinText(stop.longitude as number)}). Google may label it with a nearby address.
          </div>
        )}

        {a?.dog_present && (
          <div style={{ ...card, background: color.red, borderColor: color.redDark, color: color.white }}>
            <div style={{ fontSize: 24, fontWeight: 900 }}>DOG{a.dog_loose ? ' — LOOSE' : ''}</div>
            {foundText('dog_notes') && <div style={{ fontSize: 18, marginTop: 4, fontWeight: 800 }}>Found: {foundText('dog_notes')}</div>}
            {a.dog_notes && <div style={{ fontSize: 18, marginTop: 4 }}>{foundText('dog_notes') ? 'Customer said: ' : ''}{a.dog_notes}</div>}
          </div>
        )}

        {a?.drop_location && (
          // Where the bags go - the customer's choice, first thing after the address.
          <div style={{ ...card, textAlign: 'center' }}>
            <div style={muted}>Leave the bags at the</div>
            <div style={{ fontSize: 32, fontWeight: 900 }}>{DROP_LABEL[a.drop_location] ?? a.drop_location}</div>
            {a.drop_location === 'garage' && a.garage_code && (
              <div style={{ fontSize: 40, fontWeight: 900, letterSpacing: 6, fontVariantNumeric: 'tabular-nums', marginTop: 4 }}>{a.garage_code}</div>
            )}
            {a.drop_location === 'other' && <div style={{ ...muted, marginTop: 4 }}>See the drop-spot photo below.</div>}
          </div>
        )}

        {a?.gate_code && (
          <div style={{ ...card, textAlign: 'center' }}>
            <div style={muted}>Gate code</div>
            <div style={{ fontSize: 48, fontWeight: 900, letterSpacing: 6, fontVariantNumeric: 'tabular-nums' }}>{a.gate_code}</div>
          </div>
        )}

        {required.length > 0 && (
          <div style={card}>
            <div style={{ ...muted, marginBottom: 8 }}>Checklist · tap an item to photograph it</div>
            <div style={{ display: 'grid', gap: 8 }}>
              {required.map((t, i) => {
                const it = draft?.items[t]
                const st = itemState(it)
                const where = (ITEM_ACCESS_FIELDS[t] ?? [])
                  .map((f) => ({ label: f.label, found: foundText(f.field), value: text(f.field) }))
                  .filter((w) => w.found || w.value)
                const cond = it?.condition ? CONDITIONS[t]?.find((c) => c.value === it.condition)?.label ?? it.condition : ''
                const state = st === 'done' ? [it?.hasPhoto ? '📷' : '', cond].filter(Boolean).join(' ') : st === 'skipped' ? 'skipped' : ''
                return (
                  <button
                    key={t}
                    onClick={() => onStartAt(i)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12, width: '100%', minHeight: TAP + 8,
                      padding: '10px 12px', textAlign: 'left', font: 'inherit', color: color.ink, cursor: 'pointer',
                      background: st === 'done' ? '#ecfdf5' : color.white,
                      border: `2px solid ${st === 'done' ? color.green : color.greyLight}`, borderRadius: 12,
                    }}
                  >
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: 19, fontWeight: 800 }}>{ITEM_TITLES[t] ?? t}</span>
                      {where.map((w) => (
                        <span key={w.label} style={{ display: 'block', marginTop: 2 }}>
                          {w.found && (
                            <span style={{ display: 'block', fontSize: 16, fontWeight: 800, color: color.ink }}>
                              {where.length > 1 ? `${w.label} · ` : ''}Found: {w.found}
                            </span>
                          )}
                          {w.value && (
                            <span style={{ display: 'block', fontSize: 16, color: color.grey }}>
                              {where.length > 1 && !w.found ? `${w.label}: ` : ''}{w.found ? 'Customer said: ' : ''}{w.value}
                            </span>
                          )}
                        </span>
                      ))}
                    </span>
                    {state && (
                      <span style={{ fontSize: 16, fontWeight: 800, whiteSpace: 'nowrap', color: st === 'done' ? color.greenDark : color.grey }}>{state}</span>
                    )}
                    <span aria-hidden="true" style={{ fontSize: 28, lineHeight: 1, color: color.grey }}>›</span>
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {facts.length > 0 && (
          <div style={card}>
            {facts.map(([k, found, said]) => (
              <div key={k} style={{ marginBottom: 10 }}>
                <div style={{ ...muted, fontSize: 14, textTransform: 'uppercase', letterSpacing: 0.5 }}>{k}</div>
                {found && <div style={{ fontSize: 19, fontWeight: 800 }}>Found: {found}</div>}
                {said && <div style={{ fontSize: 19, color: found ? color.grey : color.ink }}>{found ? 'Customer said: ' : ''}{said}</div>}
              </div>
            ))}
          </div>
        )}

        {access === null && (
          <div style={{ ...card, color: color.grey }}>No access notes for this stop{navigator.onLine ? '' : ' (offline, none cached)'}.</div>
        )}
        {access && !a && access.property_capture_skipped && (
          <div style={{ ...card, background: '#fffbeb', borderColor: color.amber }}>
            <div style={{ fontWeight: 800 }}>Customer skipped the property notes.</div>
            <div style={muted}>Expect a longer first visit. Photograph the gate, storage and coop for next time.</div>
          </div>
        )}

        {access && access.reference_photos.length > 0 && (
          <div style={card}>
            <div style={{ ...muted, marginBottom: 8 }}>Reference photos</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 8 }}>
              {access.reference_photos.map((p) => (
                <figure key={p.id} style={{ margin: 0 }}>
                  <img src={photoUrls[p.id] ?? p.url} alt={p.kind} style={{ width: '100%', borderRadius: 10, aspectRatio: '4/3', objectFit: 'cover', background: color.greyLight }} />
                  <figcaption style={{ ...muted, fontSize: 14, marginTop: 4 }}>
                    {p.kind}{p.caption ? ` · ${p.caption}` : ''}
                    {p.source === 'driver' ? ' · added by a driver' : p.source === 'walk' ? ' · property walk' : p.source === 'customer' ? ' · from the customer' : ''}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        )}

        <div style={card}>
          <div style={{ ...muted, marginBottom: 6 }}>Deliver</div>
          {stop.lines.map((l, i) => (
            <div key={i} style={{ fontSize: 19, marginBottom: 2 }}>
              <strong>{l.bag_count}×</strong> {l.label}
            </div>
          ))}
        </div>

        <div style={{ marginTop: 8, marginBottom: 90, display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          <button style={button(color.white, color.blue)} onClick={onFound}>Add what you found</button>
          <button style={button(color.white, color.redDark)} onClick={onSkip}>Skip this stop</button>
        </div>
      </div>

      <div style={footer}>
        <button style={bigButton(color.green)} onClick={onStart}>
          {required.length ? (draft ? 'Continue checklist' : 'Start checklist') : 'Complete stop'} ▸
        </button>
      </div>
    </div>
  )
}
